import { useEffect, useState, type CSSProperties } from 'react';
import { api, type Player } from '../lib/api';
import { readFinal, type FinalOutcome } from '../lib/champions';
import { leagueBase } from '../lib/route';
import Crest from './Crest';

/**
 * The champions, at the top of the table, once the Final is won.
 *
 * Premier Pearls asked for "a congratulatory front page on the table" when
 * Ruby Rulos took Season 1. The table answers who finished where in the league
 * phase; this answers the question everybody arriving after the Final has,
 * which is who won it.
 *
 * EVERY LEAGUE THAT PLAYS A FINAL, not Pearls alone, and nothing to switch on:
 * it appears the moment a season's Final is saved as finished and stays as
 * that season's record, archived or not. A season with no Final, or one still
 * being played, shows nothing here at all.
 *
 * In the champion's own colour, so it is theirs rather than the league's, and
 * the confetti stops for anybody who has asked their phone for less motion.
 */
export default function ChampionsCard({
  seasonId,
  seasonName,
}: {
  seasonId?: number;
  seasonName?: string;
}) {
  const [outcome, setOutcome] = useState<FinalOutcome | null>(null);
  const [squad, setSquad] = useState<Player[]>([]);

  useEffect(() => {
    if (!seasonId) return;
    let live = true;
    setOutcome(null);
    setSquad([]);
    (async () => {
      const { gameweeks } = await api.fixtures(seasonId);
      const fixtures = gameweeks.flatMap((g) => g.fixtures);
      let reading = readFinal(fixtures);
      // A level Final is settled by its decider, which only the match carries.
      if (reading.kind === 'needsDecider') {
        const match = await api.match(reading.fixture.id);
        reading = readFinal(fixtures, match.decider?.wonBy ?? null);
      }
      if (!live || reading.kind !== 'decided') return;
      setOutcome(reading.outcome);
      const { teams } = await api.teams(seasonId);
      if (!live) return;
      setSquad(teams.find((t) => t.id === reading.outcome.champion.id)?.players ?? []);
    })().catch(() => {
      // A banner that fails to load is no banner; the table below still works.
    });
    return () => {
      live = false;
    };
  }, [seasonId]);

  if (!outcome) return null;

  const { champion, runnerUp, score, fixture } = outcome;
  const colour = champion.colour ?? 'var(--lime)';
  const playedOn = fixture.scheduledAt
    ? new Date(fixture.scheduledAt).toLocaleDateString('en-ZA', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        timeZone: 'Africa/Johannesburg',
      })
    : null;

  return (
    <section className="card champions" style={{ '--champ': colour } as CSSProperties}>
      {/* Decoration only: a few pieces of confetti in the champion's colour. */}
      <div className="champ-confetti" aria-hidden="true">
        {Array.from({ length: 18 }, (_, i) => (
          <i key={i} style={{ '--i': i } as CSSProperties} />
        ))}
      </div>

      <div className="champ-body">
        <svg className="champ-trophy" viewBox="0 0 64 64" aria-hidden="true">
          <defs>
            <linearGradient id="champ-gold" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="#fbe7a1" />
              <stop offset="0.55" stopColor="#e2b44f" />
              <stop offset="1" stopColor="#a87a22" />
            </linearGradient>
          </defs>
          <path
            fill="url(#champ-gold)"
            d="M18 6h28v6h10v6c0 8-6 14-13 15-2 5-6 8-9 9v7h8c2 0 3 1 3 3v6H19v-6c0-2 1-3 3-3h8v-7c-3-1-7-4-9-9C14 32 8 26 8 18v-6h10V6Zm-4 12c0 4 3 8 6 9-1-3-2-6-2-9h-4Zm32 0c0 3-1 6-2 9 3-1 6-5 6-9h-4Z"
          />
        </svg>

        {/* "Season 1 champions", not "Season 1 (2026) champions": the year in
            brackets is how the season list tells seasons apart, and on a
            banner it reads as a footnote in the middle of the headline. */}
        <p className="champ-kicker">
          {seasonName ? `${seasonName.replace(/\s*\(\d{4}\)\s*$/, '')} champions` : 'Champions'}
        </p>

        <div className="champ-crest">
          <Crest url={champion.logoUrl} name={champion.name} colour={champion.colour} large />
        </div>

        <h2 className="champ-name">{champion.name}</h2>

        <p className="champ-line">
          Won the Final <b>{score.champion}–{score.runnerUp}</b> against {runnerUp.name}
          {playedOn && <> &middot; {playedOn}</>}
        </p>

        {squad.length > 0 && (
          <ul className="champ-squad" aria-label={`${champion.name} squad`}>
            {/* Each name opens that player's season, as it does on Teams. */}
            {squad.map((p) => (
              <li key={p.id}>
                <a href={`${leagueBase()}/player/${p.id}`}>{p.name}</a>
              </li>
            ))}
          </ul>
        )}

        <p className="champ-congrats">Congratulations to the whole squad.</p>

        <div className="champ-foot">
          <span className="muted small">Runners-up: {runnerUp.name}</span>
          <a className="btn ghost" href={`${leagueBase()}/match/${fixture.id}`}>
            See the Final
          </a>
        </div>
      </div>
    </section>
  );
}
